// Import Firebase SDK
import { getApps, initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import { connectAuthEmulator, getAuth } from "firebase/auth";
import {
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from "firebase/firestore";
import { connectStorageEmulator, getStorage } from "firebase/storage";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { getMessaging, isSupported } from "firebase/messaging";

const firebaseConfig = {
  apiKey: "AIzaSyAv4RG-AUMeYLPsylBrORqKXwPBepOdtfA",
  authDomain: "hay-marketplace-e45da.firebaseapp.com",
  databaseURL: "https://hay-marketplace-e45da-default-rtdb.firebaseio.com",
  projectId: "hay-marketplace-e45da",
  storageBucket: "hay-marketplace-e45da.firebasestorage.app",
  messagingSenderId: "456477079775",
  appId: "1:456477079775:web:8fbdaac42019592aca916a",

};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
console.log("🔥 Firebase config projectId:", firebaseConfig.projectId);
console.log("🔥 Firebase config authDomain:", firebaseConfig.authDomain);
console.log("🔥 Firebase config storageBucket:", firebaseConfig.storageBucket);

const appCheckSiteKey = process.env.REACT_APP_APPCHECK_RECAPTCHA_V3_SITE_KEY;

if (process.env.REACT_APP_APPCHECK_DEBUG_TOKEN) {
  window.FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.REACT_APP_APPCHECK_DEBUG_TOKEN;
}

if (appCheckSiteKey) {
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(appCheckSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

// Core services (always safe)
export const auth = getAuth(app);
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});
export const storage = getStorage(app);
export const functions = getFunctions(app);

const LOCAL_LIVE_PARTICIPANT_APP_NAME = "myfarmingpal-local-live-participant";
let localLiveParticipantServices = null;

export const isLocalhostRuntime =
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

export const usingFirebaseEmulators =
  process.env.REACT_APP_USE_FIREBASE_EMULATORS === "true" &&
  isLocalhostRuntime;

export const assertSafeLocalFunctions = (featureName = "Firebase Functions") => {
  if (isLocalhostRuntime && process.env.REACT_APP_USE_FIREBASE_EMULATORS !== "true") {
    throw new Error(
      `${featureName} local testing requires REACT_APP_USE_FIREBASE_EMULATORS=true so production Functions are not invoked.`
    );
  }
};

const shouldUseFirebaseEmulators = usingFirebaseEmulators;

if (shouldUseFirebaseEmulators && !window.__MYFARMINGPAL_FIREBASE_EMULATORS__) {
  window.__MYFARMINGPAL_FIREBASE_EMULATORS__ = true;

  connectAuthEmulator(
    auth,
    process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_URL ||
      "http://127.0.0.1:9099",
    { disableWarnings: true }
  );
  connectFirestoreEmulator(
    db,
    process.env.REACT_APP_FIRESTORE_EMULATOR_HOST || "127.0.0.1",
    Number(process.env.REACT_APP_FIRESTORE_EMULATOR_PORT || 8080)
  );
  connectStorageEmulator(
    storage,
    process.env.REACT_APP_FIREBASE_STORAGE_EMULATOR_HOST || "127.0.0.1",
    Number(process.env.REACT_APP_FIREBASE_STORAGE_EMULATOR_PORT || 9199)
  );
  connectFunctionsEmulator(
    functions,
    process.env.REACT_APP_FIREBASE_FUNCTIONS_EMULATOR_HOST || "127.0.0.1",
    Number(process.env.REACT_APP_FIREBASE_FUNCTIONS_EMULATOR_PORT || 5001)
  );

  console.log("Firebase emulators enabled for local MyFarmingPal testing.");
}

export const getLocalLiveParticipantFirebaseServices = () => {
  if (localLiveParticipantServices) return localLiveParticipantServices;

  const participantApp = getApps().find((existingApp) =>
    existingApp.name === LOCAL_LIVE_PARTICIPANT_APP_NAME
  ) || initializeApp(firebaseConfig, LOCAL_LIVE_PARTICIPANT_APP_NAME);

  if (appCheckSiteKey) {
    initializeAppCheck(participantApp, {
      provider: new ReCaptchaV3Provider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  }

  const participantAuth = getAuth(participantApp);
  const participantDb = getFirestore(participantApp);
  const participantStorage = getStorage(participantApp);
  const participantFunctions = getFunctions(participantApp);

  if (
    shouldUseFirebaseEmulators &&
    typeof window !== "undefined" &&
    !window.__MYFARMINGPAL_LOCAL_LIVE_PARTICIPANT_FIREBASE_EMULATORS__
  ) {
    window.__MYFARMINGPAL_LOCAL_LIVE_PARTICIPANT_FIREBASE_EMULATORS__ = true;
    connectAuthEmulator(
      participantAuth,
      process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_URL ||
        "http://127.0.0.1:9099",
      { disableWarnings: true }
    );
    connectFirestoreEmulator(
      participantDb,
      process.env.REACT_APP_FIRESTORE_EMULATOR_HOST || "127.0.0.1",
      Number(process.env.REACT_APP_FIRESTORE_EMULATOR_PORT || 8080)
    );
    connectStorageEmulator(
      participantStorage,
      process.env.REACT_APP_FIREBASE_STORAGE_EMULATOR_HOST || "127.0.0.1",
      Number(process.env.REACT_APP_FIREBASE_STORAGE_EMULATOR_PORT || 9199)
    );
    connectFunctionsEmulator(
      participantFunctions,
      process.env.REACT_APP_FIREBASE_FUNCTIONS_EMULATOR_HOST || "127.0.0.1",
      Number(process.env.REACT_APP_FIREBASE_FUNCTIONS_EMULATOR_PORT || 5001)
    );
  }

  localLiveParticipantServices = {
    app: participantApp,
    auth: participantAuth,
    db: participantDb,
    functions: participantFunctions,
    storage: participantStorage,
  };
  return localLiveParticipantServices;
};

// 🔒 SAFE messaging (only if supported)
let messaging = null;

const initMessaging = async () => {
  try {
    const supported = await isSupported();

    if (supported) {
      messaging = getMessaging(app);
      console.log("✅ Messaging enabled");
    } else {
      console.log("❌ Messaging not supported on this device");
    }
  } catch (err) {
    console.log("Messaging check failed:", err);
  }
};

initMessaging();

export { messaging };
